import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import HeaderInstruction from "../../../aits/components/Instruction/HeaderInstruction";
import "../../../aits/pages/AitsTestInstruction.css";
import images from "../../../../utilities/images/images";
import {
  HOMEWORK_FLOW,
  PYQ_FLOW,
  fetchPyqInstructions,
  resolveFlowType,
} from "../services/exerciseExamApi";
import { getStartOrResumeTestLabel } from "../../../StudyDashboard/utils/resumeTestUtils";
import { getTopicFlowRoutes } from "../utils/topicFlowRoutes";

const DEFAULT_INSTRUCTIONS = [
  "This Practice set contains questions for the subject and topic.",
  "Users can navigate using Previous, Save & Next, and the question palette.",
  "Use Mark for Review to flag a question and come back to it later.",
  "All The Best!",
];

const ExerciseInstructionPage = ({
  exerciseData,
  pyqTestData,
  homeworkTestDetails,
  topicData,
}) => {
  const history = useHistory();
  const location = useLocation();

  const testType = resolveFlowType(location?.state, location?.pathname);
  const isPyq = testType === PYQ_FLOW;
  const isHomework = testType === HOMEWORK_FLOW;
  const isResume = location?.state?.isResume === true;
  const testData = isPyq
    ? pyqTestData
    : isHomework
      ? homeworkTestDetails
      : exerciseData;

  const [pyqInstructions, setPyqInstructions] = useState("");
  const [isLoading, setIsLoading] = useState(isPyq);

  const returnTo =
    testData?.returnTo ||
    location?.state?.returnTo ||
    (isHomework ? "/homework" : "/topic_component");
  const returnState =
    testData?.returnState || location?.state?.returnState || { tab: testType };

  const hasTestContext = isPyq
    ? Boolean(testData?.topic_name)
    : isHomework
      ? Boolean(testData?.id || testData?.paper_id)
      : Boolean(testData?.excersie_type);

  useEffect(() => {
    if (!hasTestContext) {
      history.replace({ pathname: returnTo, state: returnState });
    }
  }, [hasTestContext, history, returnTo, returnState]);

  useEffect(() => {
    if (!isPyq || !hasTestContext) return;

    let isActive = true;

    const loadInstructions = async () => {
      try {
        const instructionData = await fetchPyqInstructions(testData.topic_name);
        if (isActive) {
          setPyqInstructions(instructionData?.Instructions || "");
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadInstructions();

    return () => {
      isActive = false;
    };
  }, [isPyq, hasTestContext, testData?.topic_name]);

  if (!hasTestContext) return null;

  const homeworkTopics = Array.isArray(testData?.data)
    ? testData.data
        .map((item) => item?.topic)
        .filter(Boolean)
        .join(", ")
    : "";

  const testName = isPyq
    ? `${testData.topic_name}${
        testData.format ? ` (${String(testData.format).toUpperCase()})` : ""
      }`
    : isHomework
      ? homeworkTopics || testData?.subject || "Homework"
      : testData.excersie_type;

  const testSubtitle = isPyq
    ? testData.subject || topicData?.topicName || ""
    : isHomework
      ? testData?.subject || ""
      : topicData?.topicName || "";

  const totalQuestions = isPyq
    ? testData.total_questions
    : isHomework
      ? testData.total_questions ?? testData.total_question
      : testData.total_question;
  const durationMins = Number(testData?.duration_mins) || 0;

  const handleBack = () => {
    history.push({ pathname: returnTo, state: returnState });
  };

  const handleStartTest = () => {
    const flowRoutes = getTopicFlowRoutes(testType, location.pathname);
    history.push({
      pathname: flowRoutes.test,
      state: {
        ...(location?.state || {}),
        type: testType,
      },
    });
  };

  return (
    <div className="aitstest_instruction">
      <div className="test_container">
        <img
          src={images.new_backarrow}
          alt="back"
          className="arrow_image"
          onClick={handleBack}
        />
        <span>Test Instruction</span>
      </div>

      <div className="common_padding">
        <HeaderInstruction
          name={testName}
          type={testSubtitle}
          duration={durationMins}
          totalQuestions={totalQuestions ?? "—"}
          duration_icon={images.duriation_icon}
          hideDuration={!durationMins}
        />

        {isLoading ? (
          <div className="loading_center_container" style={{ minHeight: 160 }}>
            <ClipLoader size={36} color="#C61C26" />
          </div>
        ) : (
          <div className="general_wrapper">
            <div className="general_header">General Instructions</div>
            {isPyq && pyqInstructions ? (
              <div
                className="general_content instructions_html_container"
                dangerouslySetInnerHTML={{ __html: pyqInstructions }}
              />
            ) : (
              <div className="general_content instructions_html_container">
                {DEFAULT_INSTRUCTIONS.map((instruction) => (
                  <p key={instruction}>{instruction}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="start_btn">
          <button className="back-tests-btn" onClick={handleBack}>
            Back
          </button>

          <button className="begin-test-btn" onClick={handleStartTest}>
            <span>
              {getStartOrResumeTestLabel({ isResume })}
            </span>
            <img
              src={images.rightArrow_icon}
              alt="arrow"
              className="arrow-icon"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    exerciseData: state.userActivity.exerciseData,
    pyqTestData: state.pyqTestsData?.pyqTestData,
    homeworkTestDetails: state.HomeWorkSlice?.homeworkTestDetails,
    topicData: state.userActivity.topicData,
  };
};

export default connect(mapStateToProps)(ExerciseInstructionPage);
